<template>
  <card-dialog
    v-model="modalVisible"
    max-width="700px"
    :title="libraryFileDirty.id ? 'Edit Public Announcement' : 'Add New Public Announcement'"
  >
    <template v-slot:title>
      <v-card-title class="grey-bg px-6 py-3">
        <div>
          <div>
            {{ libraryFileDirty.id ? 'Edit Public Announcement' : 'Add New Public Announcement' }}
          </div>
          <div class="body-2">
            Client: <strong>{{ client.name }}</strong>
          </div>
        </div>
      </v-card-title>
    </template>
    <v-card-text
      id="handleDropUpload"
      class="pa-6"
    >
      <validation-observer ref="validation">
        <div class="mb-2 font-weight-bold">
          PA Title
        </div>
        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-text-field
            v-model="libraryFileDirty.title"
            outlined
            :error-messages="errors"
            placeholder="Enter PA title"
          />
        </validation-provider>

        <div class="mb-2 font-weight-bold">
          PA File
        </div>

        <div v-if="currentLibraryFiles.length > 0">
          <div
            v-for="file in currentLibraryFiles"
            :key="file.id"
            class="grey-bg elevation-5 mb-2 rounded overflow-hidden"
          >
            <div class="px-3 py-2">
              <v-row no-gutters>
                <v-col
                  cols="auto"
                  class="pr-2"
                >
                  <v-icon
                    x-small
                    @click="playAv(file)"
                  >
                    fa {{ file.isPlayingAv ? 'fa-stop' : 'fa-play' }}
                  </v-icon>
                </v-col>
                <v-col
                  class="text-truncate pr-3"
                  style="min-width: 0"
                >
                  {{ file.original_file }}
                </v-col>
                <v-col cols="auto" />
                <v-col cols="auto">
                  {{ file.size }}
                </v-col>
                <v-col
                  v-if="currentLibraryFiles.length > 0"
                  cols="auto"
                  class="pl-3"
                >
                  <v-icon
                    color="error"
                    @click="removeCurrentLibraryFile(file)"
                  >
                    fa fa-times
                  </v-icon>
                </v-col>
                <div
                  ref="wrapper"
                  class="file-av-wrapper"
                />
              </v-row>
            </div>
          </div>
        </div>

        <validation-provider
          v-slot="{ errors }"
          :rules="{ 'required': !libraryFile.id }"
        >
          <file-uploader
            ref="fileUploader"
            v-model="filesDirty"
            custom-drag-handler="#handleDropUpload"
            accept="audio/*"
            :upload-url="uploadUrl"
            :additional-form-data="additionalFormData"
            @drag:show="dropFilesOverlay = true"
            @drag:hide="dropFilesOverlay = false"
          />
          <div
            v-if="errors.length > 0"
            class="caption error--text"
          >
            {{ errors[0] }}
          </div>
        </validation-provider>

        <div class="mb-2 mt-6 font-weight-bold">
          PA Description (optional)
        </div>
        <v-textarea
          v-model="libraryFileDirty.description"
          placeholder="Enter description"
          rows="2"
          outlined
        />
      </validation-observer>
      <v-overlay
        :value="dropFilesOverlay"
        absolute
        color="white"
        light
        opacity="0.7"
      >
        <div class="text-center grey--text text--darken-2 mb-4">
          <v-icon
            x-large
            color="grey darken-2"
          >
            fa fa-clipboard-list
          </v-icon>
        </div>
        <div class="title grey--text text--darken-2">
          Drop files here
        </div>
      </v-overlay>
    </v-card-text>
    <template v-slot:actions>
      <v-card-actions class="pa-4">
        <v-btn
          v-if="libraryFileDirty.id"
          color="grey"
          outlined
          @click="toggleConfirmationDialog(true)"
        >
          <v-icon
            left
            small
          >
            fa fa-trash
          </v-icon>
          Delete
        </v-btn>
        <v-spacer />
        <div
          v-if="uploading"
          class="mr-4 body-2"
        >
          Uploading file...
        </div>
        <v-btn
          color="primary"
          :loading="submitting"
          @click="submit()"
        >
          Save
        </v-btn>
      </v-card-actions>
    </template>

    <confirm-delete-dialog
      :visible="deleteConfirmationDialogVisible"
      :submitting="deleting"
      @confirm="deleteLibraryFile()"
    />
  </card-dialog>
</template>

<script>
import { cloneDeep } from 'lodash'
import CardDialog from '@/components/helpers/CardDialog'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import LibraryFileModel from '@/models/LibraryFileModel'
// eslint-disable-next-line no-unused-vars
import FileUploader from '@/components/helpers/FileUploader'
import LibraryFiles from '@/services/LibraryFiles'

export default {
  name: 'EditPaDialog',
  components: { FileUploader, ConfirmDeleteDialog, CardDialog },
  mixins: [deleteConfirmationMixin],
  props: {
    libraryFile: {
      type: Object,
      default: () => cloneDeep(LibraryFileModel.emptyModel)
    },
    client: {
      type: Object,
      required: true
    },
    visible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      libraryFileDirty: cloneDeep(this.libraryFile),
      filesDirty: [],
      currentLibraryFiles: [],
      dropFilesOverlay: false,
      submitting: false,
      uploading: false,
      deleting: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    additionalFormData() {
      return {
        type: LibraryFileModel.TYPE_PA,
        client_id: this.client.id
      }
    }
  },
  watch: {
    filesDirty(files) {
      if (files.length === 0 || this.libraryFileDirty.title) return

      this.libraryFileDirty.title = files[0].name()
    },
    modalVisible(value) {
      if (!value) return

      this.filesDirty = []
      this.currentLibraryFiles = []

      this.libraryFileDirty = cloneDeep(this.libraryFile)

      if (this.libraryFile.id) {
        this.currentLibraryFiles = [
          {
            ...this.libraryFile,
            isPlayingAv: false,
            stopAv: null
          }
        ]
      }

      if (this.$refs.validation) this.$refs.validation.reset()
    }
  },
  created() {
    this.TYPE_PA = LibraryFileModel.TYPE_PA
    // this.fetchLibraryFiles()

    this.uploadUrl = process.env.VUE_APP_API_URL + '/library-files'
  },
  methods: {
    async submit() {
      if (!(await this.$refs.validation.validate())) {
        return
      }

      await this.save()
    },
    async save() {
      try {
        this.submitting = true

        if (this.filesDirty.length > 0) {
          this.uploading = true

          await this.$refs.fileUploader.performUpload()

          await this.$nextTick()

          this.uploading = false
        }

        const fileIds = [
          ...this.currentLibraryFiles.map(f => f.id),
          ...this.filesDirty.map(f => f.upload.data.id)
        ]

        if (fileIds.length !== 1) {
          this.$store.dispatch('alert', {
            text: 'One file for playback is required',
            color: 'error'
          })
          return
        }

        const data = {
          title: this.libraryFileDirty.title,
          description: this.libraryFileDirty.description,
          client_id: this.client.id
        }

        let result

        if (this.libraryFile.id) {
          result = await LibraryFiles.update(this.libraryFile.id, data)

          this.$emit('pa:updated', result)

          this.$store.dispatch('alert', {
            text: 'Public announcement successfully updated',
            color: 'success'
          })
        } else {
          result = await LibraryFiles.update(fileIds[0], data)

          this.$emit('pa:added', result)

          this.$store.dispatch('alert', {
            text: 'New public announcement successfully added',
            color: 'success'
          })
        }

        this.modalVisible = false
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'There were some errors submitting public announcement',
          color: 'error'
        })
        console.error(e)
        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteLibraryFile() {
      this.deleting = true

      try {
        await LibraryFiles.delete([this.libraryFileDirty.id])

        this.deleteConfirmationDialogVisible = false
        this.modalVisible = false

        this.$emit('pa:deleted', this.libraryFileDirty)

        this.$store.dispatch('alert', {
          text: 'PA was deleted',
          color: 'success'
        })
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'There was an error deleting',
          color: 'error'
        })
        throw e
      }
    },
    dragOver() {
      this.$refs.fileUploader.dragOver()
    },
    dragEnter() {
      this.$refs.fileUploader.dragEnter()
    },
    dragLeave() {
      this.$refs.fileUploader.dragLeave()
    },
    drop() {
      this.$refs.fileUploader.drop()
    },
    removeCurrentLibraryFile(file) {
      const index = this.currentLibraryFiles.findIndex(f => file.id)

      this.currentLibraryFiles.splice(index, 1)
    },
    playAv(fileRecord) {
      if (fileRecord.stopAv) {
        fileRecord.stopAv()
        return
      }

      const player = document.createElement('audio')
      player.controls = true
      player.src = fileRecord.url
      player.play()
      fileRecord.isPlayingAv = true
      fileRecord.stopAv = () => {
        player.src = ''
        fileRecord.isPlayingAv = false
        fileRecord.stopAv = null
      }
    }
  }
}
</script>
